import type { DayOfWeek, ScheduleEntry } from '@/lib/api/directory.client'
import type { TimezoneEnumValue } from '@/components/common/TimezoneSelector'
import { hasScheduleEntryEnded, toTimezoneEnumValue } from '@super/shared-types'
import { DateTime } from 'luxon'
import { formatDate } from '../formatters'

export const DOW_OPTIONS: { value: DayOfWeek; full: string; short: string }[] =
  [
    { value: 'MON', full: 'Mon', short: 'Mo' },
    { value: 'TUE', full: 'Tue', short: 'Tu' },
    { value: 'WED', full: 'Wed', short: 'We' },
    { value: 'THU', full: 'Thu', short: 'Th' },
    { value: 'FRI', full: 'Fri', short: 'Fr' },
    { value: 'SAT', full: 'Sat', short: 'Sa' },
    { value: 'SUN', full: 'Sun', short: 'Su' },
  ]

export const detectLocalTimezoneEnum = (): TimezoneEnumValue | '' => {
  try {
    const iana = Intl.DateTimeFormat().resolvedOptions().timeZone
    return toTimezoneEnumValue(iana) ?? ''
  } catch {
    return ''
  }
}

// Time off is always whole days — the editor has no time-of-day fields.
export const buildPayload = (input: {
  startDate: string
  endDate: string
  ianaTimezone: string | null
}) => {
  const { startDate, endDate, ianaTimezone } = input

  // @db.Date: store the picked day at UTC midnight; entry.timezone carries wall-clock semantics.
  const toIso = (yyyyMmDd: string): string =>
    DateTime.fromISO(yyyyMmDd, { zone: 'utc' }).toISO() ??
    `${yyyyMmDd}T00:00:00.000Z`

  return {
    startDate: toIso(startDate),
    endDate: toIso(endDate),
    daysOfWeek: [] as DayOfWeek[],
    timeOfDayStart: null,
    timeOfDayEnd: null,
    timezone: ianaTimezone ?? undefined,
    recurrenceFrequency: null,
  }
}

export const isRecurring = (entry: ScheduleEntry): boolean =>
  (entry.daysOfWeek?.length ?? 0) > 0 || entry.recurrenceFrequency === 'MONTHLY'

const startOrder = (entry: ScheduleEntry): string =>
  entry.startDate ?? entry.endDate ?? ''

// Upcoming reads soonest first; past reads most recent first.
export const partitionTimeOff = (
  entries: ScheduleEntry[],
  now: Date,
): { current: ScheduleEntry[]; past: ScheduleEntry[] } => {
  const current: ScheduleEntry[] = []
  const past: ScheduleEntry[] = []
  for (const entry of entries) {
    if (hasScheduleEntryEnded(entry, now)) past.push(entry)
    else current.push(entry)
  }
  // Entries already under way rank as today, not by their old start date.
  const today = DateTime.fromJSDate(now).toISODate() ?? ''
  const upcomingOrder = (entry: ScheduleEntry): string => {
    const start = entry.startDate?.slice(0, 10) ?? ''
    return start > today ? start : today
  }
  current.sort((left, right) =>
    upcomingOrder(left).localeCompare(upcomingOrder(right)),
  )
  past.sort((left, right) => startOrder(right).localeCompare(startOrder(left)))
  return { current, past }
}

export const ordinalDayOfMonth = (day: number): string => {
  if (day === -1) return 'Last day'
  const onesDigit = day % 10
  const lastTwoDigits = day % 100
  if (lastTwoDigits >= 11 && lastTwoDigits <= 13) return `${day}th`
  if (onesDigit === 1) return `${day}st`
  if (onesDigit === 2) return `${day}nd`
  if (onesDigit === 3) return `${day}rd`
  return `${day}th`
}

export const WEEKDAYS = new Set<DayOfWeek>(['MON', 'TUE', 'WED', 'THU', 'FRI'])
export const WEEKENDS = new Set<DayOfWeek>(['SAT', 'SUN'])
export const DOW_3LETTER: Record<DayOfWeek, string> = {
  SUN: 'Sun',
  MON: 'Mon',
  TUE: 'Tue',
  WED: 'Wed',
  THU: 'Thu',
  FRI: 'Fri',
  SAT: 'Sat',
}
export const DOW_ORDER: DayOfWeek[] = [
  'MON',
  'TUE',
  'WED',
  'THU',
  'FRI',
  'SAT',
  'SUN',
]

export const formatDaysOfWeek = (days: DayOfWeek[]): string => {
  const daySet = new Set(days)
  if (daySet.size === 7) return 'Daily'
  if (
    daySet.size === WEEKDAYS.size &&
    [...WEEKDAYS].every(day => daySet.has(day))
  ) {
    return 'Weekdays'
  }
  if (
    daySet.size === WEEKENDS.size &&
    [...WEEKENDS].every(day => daySet.has(day))
  ) {
    return 'Weekends'
  }
  return DOW_ORDER.filter(day => daySet.has(day))
    .map(day => DOW_3LETTER[day])
    .join(', ')
}

export const formatScheduleFallback = (
  entry: ScheduleEntry,
  options: {
    staffById: Record<string, { name: string }>
    roleById: Record<string, { name: string }>
  },
): string => {
  if (!entry.substitutes?.length) return ''
  const names = [...entry.substitutes]
    .sort((left, right) => left.position - right.position)
    .map(substitute => {
      if (substitute.staffId) {
        return options.staffById[substitute.staffId]?.name ?? '(missing)'
      }
      if (substitute.roleId) {
        const roleName =
          options.roleById[substitute.roleId]?.name ?? '(missing)'
        return `Role: ${roleName}`
      }
      return '(missing)'
    })
  return names.join(', ')
}

export const formatScheduleWhen = (entry: ScheduleEntry): string => {
  if (entry.preset === 'BUSINESS_HOURS') return 'Business Hours Only'
  if (entry.preset === 'AFTER_HOURS') return 'After Hours Only'
  if (entry.preset === 'ALWAYS') return 'Always'
  if (isRecurring(entry)) {
    const time =
      entry.timeOfDayStart && entry.timeOfDayEnd
        ? `${entry.timeOfDayStart}–${entry.timeOfDayEnd}`
        : 'all day'
    const tz = entry.timezone ? (entry.timezone.split('/').pop() ?? '') : ''

    if (entry.recurrenceFrequency === 'MONTHLY') {
      const day =
        entry.dayOfMonth !== null ? ordinalDayOfMonth(entry.dayOfMonth) : '—'
      return `Monthly · ${day} · ${time}${tz ? ` · ${tz}` : ''}`
    }

    const dow = formatDaysOfWeek(entry.daysOfWeek)
    const prefix = entry.recurrenceFrequency === 'BIWEEKLY' ? 'Biweekly · ' : ''
    return `${prefix}${dow} · ${time}${tz ? ` · ${tz}` : ''}`
  }
  if (entry.startDate && entry.endDate) {
    return `${formatDate(entry.startDate)} – ${formatDate(entry.endDate)}`
  }
  return 'Always'
}
